import React, { useEffect, useState, useRef } from 'react';
import { Paper, Typography, Box, Grid, useTheme, alpha, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDashboard } from '../../contexts/DashboardContext';
import {
  CheckCircle as QualifiedIcon,
  Whatshot as HotLeadsIcon,
  LocalFireDepartment as WarmLeadsIcon,
  AcUnit as ColdLeadsIcon,
} from '@mui/icons-material';

const funnelMeta = [
  {
    key: 'qualified_leads',
    title: 'Qualified',
    icon: <QualifiedIcon />,
    color: '#10b981',
    stat_type: 'qualified-leads',
    description: 'Leads in qualified stage',
  },
  {
    key: 'hot_leads',
    title: 'Hot',
    icon: <HotLeadsIcon />,
    color: '#dc2626',
    stat_type: 'hot-leads',
    description: 'Leads marked as hot',
  },
  {
    key: 'warm_leads',
    title: 'Warm',
    icon: <WarmLeadsIcon />,
    color: '#f59e0b',
    stat_type: 'warm-leads',
    description: 'Leads marked as warm',
  },
  {
    key: 'cold_leads',
    title: 'Cold',
    icon: <ColdLeadsIcon />,
    color: '#60a5fa',
    stat_type: 'cold-leads',
    description: 'Leads marked as cold',
  },
];

const defaultFunnelStats = {
  qualified_leads: { value: 0 },
  hot_leads: { value: 0 },
  warm_leads: { value: 0 },
  cold_leads: { value: 0 },
};

// Sample data by source for design (Active Lead Funnel)
const SAMPLE_FUNNEL_BY_SOURCE = {
  all: { qualified_leads: 12, hot_leads: 8, warm_leads: 24, cold_leads: 15 },
  website: { qualified_leads: 5, hot_leads: 4, warm_leads: 10, cold_leads: 6 },
  referral: { qualified_leads: 3, hot_leads: 2, warm_leads: 6, cold_leads: 4 },
  facebook: { qualified_leads: 2, hot_leads: 1, warm_leads: 5, cold_leads: 3 },
  google: { qualified_leads: 4, hot_leads: 3, warm_leads: 8, cold_leads: 5 },
  walk_in: { qualified_leads: 1, hot_leads: 2, warm_leads: 3, cold_leads: 2 },
};

const FunnelCard = ({ title, value, icon, color, statType, selectedTeams = [], selectedAgents = [] }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const handleCardClick = () => {
    if (!statType) return;
    const params = new URLSearchParams();
    params.set('stat_type', statType);
    const currentUrl = new URL(window.location.href);
    const filterId = currentUrl.searchParams.get('filter_id');
    const startDate = currentUrl.searchParams.get('start_date');
    const endDate = currentUrl.searchParams.get('end_date');
    if (selectedTeams?.length > 0) params.set('teams', selectedTeams.join(','));
    if (selectedAgents?.length > 0) params.set('agents', selectedAgents.join(','));
    navigate(`/leads?${params.toString()}`, {
      state: {
        from: 'dashboard',
        dashboardFilter: filterId,
        dashboardStartDate: startDate,
        dashboardEndDate: endDate,
        selectedTeams,
        selectedAgents,
      },
    });
  };

  return (
    <Paper
      elevation={0}
      onClick={handleCardClick}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${alpha(color, 0.2)}`,
        background: 'background.paper',
        boxShadow: `0 2px 8px ${alpha(color, 0.08)}`,
        transition: 'all 0.3s ease',
        height: '100%',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: `0 4px 16px ${alpha(color, 0.15)}`,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              fontWeight: 500,
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 'bold',
              color: color,
              fontSize: '1.75rem',
            }}
          >
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: alpha(color, 0.1),
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
      </Box>
    </Paper>
  );
};

// Derive funnel stats from raw pending-actions response (same data as Pending Action)
const funnelStatsFromResponse = (response) => {
  if (!response) return defaultFunnelStats;
  const getVal = (key) => {
    const v = response[key];
    if (v != null && typeof v === 'object' && 'value' in v) return v.value;
    if (typeof v === 'number') return v;
    return 0;
  };
  return {
    qualified_leads: { value: getVal('qualified_leads') },
    hot_leads: { value: getVal('hot_leads') },
    warm_leads: { value: getVal('warm_leads') },
    cold_leads: { value: getVal('cold_leads') },
  };
};

const ActiveLeadFunnel = ({
  selectedTeams = [],
  selectedAgents = [],
  selectedSources = [],
  externalIntentResponse,
  externalIntentLoading,
  externalPendingResponse,
  externalPendingLoading,
  useSampleData = false,
}) => {
  const theme = useTheme();
  const { fetchPendingActions } = useDashboard();
  const [stats, setStats] = useState(defaultFunnelStats);
  const [loading, setLoading] = useState(false);
  const lastFetchRef = useRef('');

  const useIntentEndpoint = typeof externalIntentResponse !== 'undefined';
  const useExternalPending = !useIntentEndpoint && !useSampleData && typeof externalPendingResponse !== 'undefined';
  const sampleStats = (() => {
    const keys = selectedSources.length === 0 ? ['all'] : selectedSources;
    const combined = keys.reduce(
      (acc, key) => {
        const raw = SAMPLE_FUNNEL_BY_SOURCE[key] || SAMPLE_FUNNEL_BY_SOURCE.all;
        acc.qualified_leads += raw.qualified_leads;
        acc.hot_leads += raw.hot_leads;
        acc.warm_leads += raw.warm_leads;
        acc.cold_leads += raw.cold_leads;
        return acc;
      },
      { qualified_leads: 0, hot_leads: 0, warm_leads: 0, cold_leads: 0 }
    );
    return {
      qualified_leads: { value: combined.qualified_leads },
      hot_leads: { value: combined.hot_leads },
      warm_leads: { value: combined.warm_leads },
      cold_leads: { value: combined.cold_leads },
    };
  })();
  const effectiveStats = useIntentEndpoint
    ? funnelStatsFromResponse(externalIntentResponse)
    : useSampleData
      ? sampleStats
      : useExternalPending
        ? funnelStatsFromResponse(externalPendingResponse)
        : stats;
  const effectiveLoading = useIntentEndpoint
    ? externalIntentLoading
    : useSampleData
      ? false
      : useExternalPending
        ? externalPendingLoading
        : loading;

  useEffect(() => {
    if (useIntentEndpoint || useExternalPending) return;
    const teamFilterKey = selectedTeams.join(',');
    const agentFilterKey = selectedAgents.join(',');
    const fetchKey = `active_funnel_${teamFilterKey}_${agentFilterKey}`;
    if (fetchKey === lastFetchRef.current) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await fetchPendingActions(selectedTeams, selectedAgents);
        setStats(funnelStatsFromResponse(response));
        lastFetchRef.current = fetchKey;
      } catch (e) {
        console.error('ActiveLeadFunnel: Error fetching funnel stats:', e);
        setStats(defaultFunnelStats);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [useIntentEndpoint, useExternalPending, selectedTeams, selectedAgents, fetchPendingActions]);

  if (effectiveLoading) {
    return (
      <Grid container spacing={3}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                background: 'background.paper',
                height: '100%',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="40%" height={40} />
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Skeleton variant="circular" width={24} height={24} />
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      {funnelMeta.map((meta) => {
        const stat = effectiveStats[meta.key];
        const value = stat?.value ?? (typeof stat === 'number' ? stat : '--');
        return (
          <Grid item xs={12} sm={6} md={3} key={meta.key}>
            <FunnelCard
              title={meta.title}
              value={value}
              icon={meta.icon}
              color={meta.color}
              statType={meta.stat_type}
              selectedTeams={selectedTeams}
              selectedAgents={selectedAgents}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default React.memo(ActiveLeadFunnel);
